import * as React from "react";
import { HelpCircle, AlertTriangle, Trash2, CheckCircle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type IconVariant = "help" | "warning" | "danger" | "success";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon?: React.ReactNode;
  variant?: IconVariant;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  loading?: boolean;
}

const variantStyles: Record<IconVariant, { bg: string; icon: string }> = {
  help: { bg: "bg-[rgba(0,0,0,0.05)]", icon: "#000000" },
  warning: { bg: "bg-[rgba(249,115,22,0.09)]", icon: "#C4321A" },
  danger: { bg: "bg-[rgba(0,0,0,0.05)]", icon: "#000000" },
  success: { bg: "bg-[rgba(0,170,11,0.09)]", icon: "#1C3D22" },
};

const variantIcons: Record<IconVariant, React.ReactNode> = {
  help: <HelpCircle className="h-6 w-6" />,
  warning: <AlertTriangle className="h-6 w-6" />,
  danger: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      className="lucide lucide-circle-question-mark-icon lucide-circle-question-mark"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  ),
  success: <CheckCircle className="h-6 w-6" />,
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  icon,
  variant = "help",
  title,
  description,
  confirmText = "확인",
  cancelText = "취소",
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const styles = variantStyles[variant];
  const defaultIcon = variantIcons[variant];

  const handleConfirm = () => {
    if (loading) return;
    onConfirm?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    if (loading) return;
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-[4px] p-6 shadow-lg max-w-[500px] w-full border-0">
        <div className="flex flex-col items-start gap-4">
          {/* Icon */}
          <div
            className={cn("w-12 h-12 rounded-[4px] flex items-center justify-center bg-[#EBEBEB]")}
          >
            {icon || defaultIcon}
          </div>

          <div className="flex flex-col gap-2 items-start">
            {" "}
            {/* Title */}
            <h2 className="text-[24px] font-bold text-[#000000] leading-tight">{title}</h2>
            {/* Description */}
            {description && (
              <p className="text-[14px] text-[#000000] leading-relaxed whitespace-pre-line text-left">{description}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 w-full mt-2">
            <Button
              onClick={handleCancel}
              disabled={loading}
              variant="secondary"
              className="h-12 flex-1 rounded-[8px] text-[#000000] font-bold text-[14px] outline-none"
            >
              {cancelText}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading}
              variant="default"
              className="h-12 flex-1 rounded-[4px] font-bold text-[14px]"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {confirmText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ConfirmDialog.displayName = "ConfirmDialog";

export { ConfirmDialog };
export type { ConfirmDialogProps };
